import React from "react";
import { useQuery } from "@apollo/client";
import Typography from "@mui/material/Typography";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { useCompanyPageStore } from "../../../../../store/companyPageStore";
import { TAXONOMY_CLIENT } from "../../../../../utils/consts";
import { Duns } from "../../../../../legacy/types/SpogCompany";
import { DUNS_QUERY } from "../../../../../apis/taxonomy/queries";

// Copied from MUI docs
const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const SkeletonContent: React.FC = () => (
  <Stack direction="column" gap={1} minWidth={240} p={1}>
    <Stack direction="column">
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="80%" />
    </Stack>
    <Stack direction="column">
      <Skeleton variant="text" width="40%" />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="70%" />
      <Skeleton variant="text" width="50%" />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="40%" />
    </Stack>
  </Stack>
);

// Internal component to handle query and Tooltip rendering when DUNS ID is present
interface DunsTooltipRendererProps {
  dunsId: string;
  children: React.ReactNode;
}

const DunsTooltipRenderer: React.FC<DunsTooltipRendererProps> = ({
  dunsId,
  children,
}) => {
  const { data, loading, error } = useQuery<{ duns: Duns }>(DUNS_QUERY, {
    variables: { dunsId: +dunsId },
    context: { clientName: TAXONOMY_CLIENT },
  });

  const TooltipContent: React.FC = () => {
    if (loading) return <SkeletonContent />;
    if (error)
      return (
        <Typography variant="caption" color="error">
          Error loading DUNS details.
        </Typography>
      );
    if (!data || !data.duns)
      return (
        <Typography variant="caption">No DUNS details available.</Typography>
      );

    const { companyName, streetAddress, city, state, zipCode, country } =
      data.duns;

    return (
      <Stack direction="column" gap={1} minWidth={240} p={1}>
        <Stack direction="column">
          <Typography variant="subtitle1" fontWeight="bold">
            Company Name
          </Typography>
          <Typography variant="body2">{companyName}</Typography>
        </Stack>
        <Stack direction="column">
          <Typography variant="subtitle1" fontWeight="bold">
            Address
          </Typography>
          <Typography variant="body2">
            {streetAddress && (
              <>
                {streetAddress} <br />
              </>
            )}
            {city && (
              <>
                {city} <br />
              </>
            )}
            {state && (
              <>
                {state} <br />
              </>
            )}
            {zipCode && (
              <>
                {zipCode} <br />
              </>
            )}
            {country}
          </Typography>
        </Stack>
      </Stack>
    );
  };

  return (
    <LightTooltip title={<TooltipContent />} placement="top">
      {/* MUI Tooltip will automatically wrap children if needed, or attach to the single child element */}
      <span>{children}</span>
    </LightTooltip>
  );
};

interface CompanyDunsDetailsProps {
  children: React.ReactNode;
}

export const CompanyDunsDetails: React.FC<CompanyDunsDetailsProps> = ({
  children,
}) => {
  const duns = useCompanyPageStore((state) => state.company?.duns);

  if (!duns) {
    // If no DUNS number for the company, render children as-is, without a tooltip.
    return <>{children}</>;
  }

  // If DUNS number exists, render children wrapped by the tooltip logic component.
  return <DunsTooltipRenderer dunsId={duns}>{children}</DunsTooltipRenderer>;
};
